import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   Input,
   OnDestroy,
   OnInit,
   ViewEncapsulation,
} from '@angular/core';
import { ControlContainer, FormGroupDirective, UntypedFormGroup, Validators } from '@angular/forms';
import { ExtensionTypes, NullActionTypes } from 'generated/graphql-types';
import { Subject } from 'rxjs';
import { distinctUntilChanged, skip, takeUntil } from 'rxjs/operators';
import { copyNameControlToShortNameAndDisplayName } from 'src/app/common-components/helpers/extension-form-helper';
import { ExtensionOption } from 'src/app/pages/extension-register/models/extension-option.interface';
import { getNullActionPlaceholder, getNullActionTypeNumericValue } from '../../helpers/null-action-helper';
import { ExtensionTypeDetails } from '../../models/extension-type-details';
import { GetExtensionDetail_extension_extensionAutoAttendant } from '../../queries/generated/GetExtensionDetail';

@Component({
   changeDetection: ChangeDetectionStrategy.OnPush,
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-extension-auto-attendant-details',
   styleUrls: ['./extension-auto-attendant-details.component.scss'],
   templateUrl: './extension-auto-attendant-details.component.html',
   standalone: false,
})
export class ExtensionAutoAttendantDetailsComponent extends ExtensionTypeDetails implements OnInit, OnDestroy {
   @Input() extensionAutoAttendant: GetExtensionDetail_extension_extensionAutoAttendant | null;

   @Input() accountId: number | null;

   @Input() extensionId: number | null;

   form: UntypedFormGroup;

   destroyed$ = new Subject<void>();

   extensionNumber: number | null;

   extensionName: string | null;

   timeoutPlaceholder: string;

   nullActions: NullActionTypes[] = [NullActionTypes.Directory, NullActionTypes.Hangup];

   isTimeoutExtensionTypePhone: boolean;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      private controlContainer: ControlContainer,
      private parentForm: FormGroupDirective
   ) {
      super();
   }

   ngOnDestroy(): void {
      this.extensionAutoAttendant = null;
      this.destroyed$.next();
   }

   ngOnInit(): void {
      this.form = <UntypedFormGroup>this.controlContainer.control;

      const useTimeoutToggleControl = this.parentForm.form.get('extensionAutoAttendant.useTimeoutToggle');
      const timeoutExtensionControl = this.parentForm.form.get('extensionAutoAttendant.timeoutExtension');
      const timeoutNullAction = this.parentForm.form.get('extensionAutoAttendant.timeoutNullAction');
      const timeoutSecondsControl = this.parentForm.form.get('extensionAutoAttendant.timeoutSeconds');
      const isTimeoutVoicemailOnlyControl = this.parentForm.form.get('extensionAutoAttendant.isTimeoutVoicemailOnly');
      const numberControl = this.parentForm.form.get('number');
      const nameControl = this.parentForm.form.get('name');

      if (this.parentForm.disabled) {
         this.form.disable();
         return;
      }
      copyNameControlToShortNameAndDisplayName(this.parentForm.form, this.form);

      numberControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value: number | null) => {
         this.extensionNumber = value;
      });
      nameControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value: string | null) => {
         this.extensionName = value;
      });
      timeoutNullAction?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value: NullActionTypes) => {
         this.timeoutPlaceholder = getNullActionPlaceholder(value);
      });
      timeoutExtensionControl?.valueChanges
         .pipe(
            takeUntil(this.destroyed$),
            distinctUntilChanged((a, b) => a?.extensionId === b?.extensionId),
            skip(1)
         )
         .subscribe((value: ExtensionOption | null) => {
            if (!value) return;

            this.isTimeoutExtensionTypePhone = value?.type === ExtensionTypes.Phone && value.extensionId >= 0;

            const timeoutVoicemailOnly = this.form.get('isTimeoutVoicemailOnly');

            if (!this.isTimeoutExtensionTypePhone) {
               timeoutVoicemailOnly?.setValue(false, { emitEvent: false });
               timeoutVoicemailOnly?.markAsDirty();
               timeoutVoicemailOnly?.updateValueAndValidity();
            }

            const nullAction = this.getNullActionFromValue(value);

            if (nullAction !== NullActionTypes.Unset) {
               timeoutExtensionControl.setValue(
                  {
                     extensionId: null,
                     name: getNullActionPlaceholder(nullAction),
                     number: getNullActionTypeNumericValue(nullAction),
                  },
                  { emitEvent: false }
               );
            }

            timeoutNullAction?.setValue(nullAction);

            timeoutExtensionControl?.markAsDirty();
            timeoutNullAction?.markAsDirty();

            this.timeoutPlaceholder = getNullActionPlaceholder(nullAction);
         });

      this.isTimeoutExtensionTypePhone = this.extensionAutoAttendant?.timeoutExtension?.type === ExtensionTypes.Phone;

      const applyTimeoutEnabledState = (useToggle: boolean): void => {
         if (useToggle) {
            timeoutSecondsControl?.enable();
            timeoutSecondsControl?.setValidators([Validators.required, Validators.min(1), Validators.max(10)]);
            timeoutExtensionControl?.enable();
            timeoutExtensionControl?.setValidators([Validators.required]);
         } else {
            timeoutSecondsControl?.disable();
            timeoutExtensionControl?.disable();
         }
         timeoutSecondsControl?.updateValueAndValidity();
         timeoutExtensionControl?.updateValueAndValidity();
      };

      useTimeoutToggleControl?.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((useToggle) => {
         applyTimeoutEnabledState(useToggle);

         if (useToggle) {
            timeoutSecondsControl?.markAsTouched();
            timeoutExtensionControl?.markAsTouched();
         } else {
            timeoutSecondsControl?.setValue(null);
            timeoutSecondsControl?.markAsDirty();
            timeoutExtensionControl?.setValue(null);
            timeoutExtensionControl?.markAsDirty();
            timeoutNullAction?.setValue(NullActionTypes.Unset);
            timeoutNullAction?.markAsDirty();
            isTimeoutVoicemailOnlyControl?.setValue(false);
            isTimeoutVoicemailOnlyControl?.markAsDirty();
            this.isTimeoutExtensionTypePhone = false;
         }
      });

      const useTimeoutInitially = !!this.extensionAutoAttendant && this.extensionAutoAttendant.timeoutSeconds !== null;
      useTimeoutToggleControl?.setValue(useTimeoutInitially, { emitEvent: false });
      applyTimeoutEnabledState(useTimeoutInitially);
   }

   handleTimeoutExtensionChange() {
      const selectedTimeoutExtension = this.form.get('timeoutExtension')?.value;
      const timeoutVoicemailOnly = this.form.get('isTimeoutVoicemailOnly');
      if (!selectedTimeoutExtension) {
         return;
      }
      this.isTimeoutExtensionTypePhone =
         selectedTimeoutExtension?.type === ExtensionTypes.Phone && selectedTimeoutExtension?.extensionId >= 0;
      if (!this.isTimeoutExtensionTypePhone) {
         timeoutVoicemailOnly?.setValue(false);
         timeoutVoicemailOnly?.markAsDirty();
         timeoutVoicemailOnly?.updateValueAndValidity();
      }
   }

   private getNullActionFromValue(value: ExtensionOption): NullActionTypes {
      switch (value.number) {
         case -1:
            return NullActionTypes.Directory;
         case -2:
            return NullActionTypes.Hangup;
         default:
            return NullActionTypes.Unset;
      }
   }
}
